import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import type { ProductCatalogCreate, ProductCatalogItem, ProductCatalogUpdate } from '../../utils/productCatalogApi';

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  EQUIPMENT: 'Equipment',
  CONSUMABLE: 'Consumable',
  FOOD: 'Food',
  SUPPLEMENT: 'Supplement',
  ACCESSORY: 'Accessory',
  OTHER: 'Other',
};

type Mode = 'create' | 'edit';

export const ProductCatalogItemDrawer: React.FC<{
  isOpen: boolean;
  mode: Mode;
  item: ProductCatalogItem | null;
  onClose: () => void;
  onSave: (data: ProductCatalogCreate | ProductCatalogUpdate) => Promise<void>;
  headerTitle?: string;
}> = ({ isOpen, mode, item, onClose, onSave, headerTitle }) => {
  const [name, setName] = useState('');
  const [productType, setProductType] = useState('OTHER');
  const [description, setDescription] = useState('');
  const [sku, setSku] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [isStockTracked, setIsStockTracked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    if (item) {
      setName(item.name || '');
      setProductType(item.product_type || 'OTHER');
      setDescription(item.description || '');
      setSku(item.sku || '');
      setManufacturer(item.manufacturer || '');
      setIsStockTracked(item.is_stock_tracked ?? false);
    } else {
      setName('');
      setProductType('OTHER');
      setDescription('');
      setSku('');
      setManufacturer('');
      setIsStockTracked(false);
    }
  }, [isOpen, item?.id]);

  const title = useMemo(() => {
    if (headerTitle) return headerTitle;
    if (mode === 'create') return item?.is_standard ? 'Customize product' : 'Add product';
    return 'Edit product';
  }, [headerTitle, mode, item?.is_standard]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (mode === 'create') {
        const payload: ProductCatalogCreate = {
          name: name.trim(),
          product_type: productType,
          description: description || undefined,
          sku: sku || undefined,
          manufacturer: manufacturer || undefined,
          is_stock_tracked: isStockTracked,
          ...(item?.is_standard ? { based_on_standard_id: item.id } : {}),
        };
        await onSave(payload);
      } else {
        const payload: ProductCatalogUpdate = {
          name: name.trim(),
          product_type: productType,
          description: description || undefined,
          sku: sku || undefined,
          manufacturer: manufacturer || undefined,
          is_stock_tracked: isStockTracked,
        };
        await onSave(payload);
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl border-l border-gray-200 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">{title}</div>
            <div className="text-xs text-gray-500 mt-0.5 truncate">
              Update product details and inventory settings.
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-gray-100 text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(PRODUCT_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
              <input
                type="text"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
              <input
                type="text"
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={() => setIsStockTracked((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isStockTracked ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isStockTracked ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <div>
              <div className="text-sm font-medium text-gray-900">Track as inventory</div>
              <div className="text-xs text-gray-500">Enables on-hand, lots, and inventory reporting.</div>
            </div>
          </div>

        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};


